<template>
    <Toast />
    <div class="grid reporting">
        <div class="col-12">
            <div class="card">
                <h5>Vendor Partner Ranking</h5>
                <p>View and manage vendor rankings, RFP invitations, and performance metrics.</p>

                <div class="upload-section">
                    <div class="upload-header">
                        <h6>Update Vendor Ranks</h6>
                        <div class="upload-description">
                            <div class="instructions-title">To update ranks:</div>
                            <ul class="instructions-list">
                                <li>Download current vendor rank data</li>
                                <li>Edit the values in Excel</li>
                                <li>Upload the updated file below</li>
                            </ul>
                        </div>
                    </div>
                    <div class="upload-controls">
                        <div class="upload-actions">
                            <Button 
                                label="Download Current Data" 
                                icon="pi pi-download" 
                                class="p-button"
                                @click="downloadRanking"
                                :disabled="loading || partnerList.length === 0"
                            />
                            <FileUpload 
                                mode="basic" 
                                :customUpload="true" 
                                @uploader="uploadExcelFile" 
                                accept=".xlsx,.xls" 
                                :auto="true"
                                chooseLabel="Upload Updated File" 
                                class="uploader fixed-width-btn" 
                                />
                            </div>
                        <small class="upload-hint">Only .xlsx and .xls files are allowed.</small>
                        </div>
                    <ProgressBar v-if="isUploading" :value="uploadProgress" />
                    </div>

                <DataTable 
                    :value="partnerList" 
                    :loading="loading"    
                    :scrollable="true" 
                    responsiveLayout="scroll"
                    :paginator="true"
                    :rows="50"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} vendors"
                    :sortField="'rank'"
                    :sortOrder="1"
                >
                    <template #empty>
                        <div class="p-text-center">No vendor data available.</div>
                    </template>
                    
                    <Column field="vendor_name" header="Vendor Name" sortable style="min-width: 15rem">
                        <template #body="slotProps">
                            {{ slotProps.data.vendor_name || slotProps.data.name || 'N/A' }}
                        </template>
                    </Column>
                    
                    <Column field="rfps_invited" header="RFPs Invited" sortable dataType="numeric" style="min-width: 10rem">
                        <template #body="slotProps">
                            {{ slotProps.data.rfps_invited || slotProps.data.rfp_invited || 0 }}
                        </template>
                    </Column>
                    
                    <Column field="rfps_won" header="RFPs Won" sortable dataType="numeric" style="min-width: 10rem">
                        <template #body="slotProps">
                            {{ slotProps.data.rfps_won || slotProps.data.rfp_won || 0 }}
                        </template>
                    </Column>
                                        
                    <Column field="specialMarketing" header="Marketing Profile" sortable style="min-width: 12rem">
                        <template #body="slotProps">
                            {{ slotProps.data.specialMarketing === true ? 'Yes' : slotProps.data.specialMarketing === false ? 'No' : 'N/A' }}
                        </template>
                    </Column>

                      
                    <Column field="accredited" header="Accredited" sortable style="min-width: 10rem">
                        <template #body="slotProps">
                            {{ slotProps.data.accredited === true ? 'Yes' : slotProps.data.accredited === false ? 'No' : 'N/A' }}
                        </template>
                    </Column>

                    
                    <Column field="rank" header="Vendor Rank" sortable dataType="numeric" style="min-width: 10rem">
                        <template #body="slotProps">
                            {{ slotProps.data.rank !== null && slotProps.data.rank !== undefined ? slotProps.data.rank : 'N/A' }}
                        </template>
                    </Column>
                    
                    <Column field="rank_boost" header="Vendor Boost" sortable dataType="numeric" style="min-width: 10rem">
                        <template #body="slotProps">
                            {{ slotProps.data.rank_boost !== null && slotProps.data.rank_boost !== undefined ? slotProps.data.rank_boost : 'N/A' }}
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import FileUpload from 'primevue/fileupload';
import ProgressBar from 'primevue/progressbar';
import * as XLSX from 'xlsx';
import PartnerRankingService from '../service/PartnerRankingService';

const partnerRankingService = new PartnerRankingService();

export default {
    name: 'PartnerVendorRanking',
    components: {
        DataTable,
        Column,
        Button,
        FileUpload,
        ProgressBar
    },
        data() {
            return {
            partnerList: [],
            loading: false,
            isUploading: false,
            uploadProgress: 0
            };
        },
    async created() {
        await this.loadPartnerList();
    },
    methods: {
        async loadPartnerList() {
            this.loading = true;
            try {
                const response = await partnerRankingService.getPartnerList();
                
                // Check if response has data
                if (response && response.data) {
                    // Handle different response structures
                    if (Array.isArray(response.data)) {
                        this.partnerList = response.data;
                    } else if (response.data.data && Array.isArray(response.data.data)) {
                        this.partnerList = response.data.data;
                    } else if (response.data.partners && Array.isArray(response.data.partners)) {
                        this.partnerList = response.data.partners;
                    } else {
                        console.warn('Unexpected response structure:', response);
                        this.partnerList = [];
                    }
                } else {
                    this.partnerList = [];
                }
            } catch (error) {
                console.error('Error loading partner list:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load partner list. Please try again.',
                    life: 5000
                });
                this.partnerList = [];
            } finally {
                this.loading = false;
            }
        },

        async uploadExcelFile(event) {
            const file = event.files[0];
            if (!file) {
                return;
            }

            this.isUploading = true;
            this.uploadProgress = 0;

            try {
                // Basic validation (extension/content-type)
                const validationResult = await this.validateExcelFile(file);
                if (!validationResult.isValid) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Invalid Excel File',
                        detail: validationResult.error,
                        life: 5000
                    });
                    return;
                }
                this.uploadProgress = 25;

                // 1) Get presigned URL
                const timestamp = new Date();
                const pad = (n) => String(n).padStart(2, '0');
                const stamp = `${timestamp.getFullYear()}-${pad(timestamp.getMonth()+1)}-${pad(timestamp.getDate())}-${pad(timestamp.getHours())}-${pad(timestamp.getMinutes())}`;
                const targetFileName = `ranking-${stamp}.xlsx`;
                const contentType = file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                const presignedRes = await partnerRankingService.getPresignedUrl(targetFileName, contentType);
                const uploadURL = presignedRes?.data?.uploadURL || presignedRes?.data?.uploadUrl || presignedRes?.data?.url;
                const key = presignedRes?.data?.Key || presignedRes?.data?.key;

                if (!uploadURL || !key) {
                    throw new Error('Failed to obtain upload URL.');
                }

                this.uploadProgress = 55;

                // 2) Upload file to storage
                await partnerRankingService.uploadToS3(uploadURL, file);

                this.uploadProgress = 80;

                // 3) Notify backend for processing
                const subnetworkId = process.env.VUE_APP_DEFAULT_SUBNETWORK_ID;
                const notifyRes = await partnerRankingService.notifyUpload(key, subnetworkId);

                this.uploadProgress = 100;

                if (notifyRes && (notifyRes.status === 200 || notifyRes.status === 201)) {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Upload Successful',
                        detail: 'Vendor ranking file uploaded. Processing has been started.',
                        life: 5000
                    });

                    // Reload partner list to show updated ranks
                    await this.loadPartnerList();
                } else {
                    throw new Error(notifyRes?.data?.message || 'Failed to notify backend of upload');
                }
            } catch (error) {
                console.error('Error uploading Excel file:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Upload Failed',
                    detail: error.message || 'Failed to upload vendor ranking file.',
                    life: 5000
                });
            } finally {
                this.isUploading = false;
                this.uploadProgress = 0;
                // Clear the file input
                if (event && event.target) {
                    event.target.value = '';
                }
            }
        },

        async validateExcelFile(file) {
            return new Promise((resolve) => {
                // Check file extension
                const fileName = file.name.toLowerCase();
                if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
                    resolve({
                        isValid: false,
                        error: 'Please upload a valid Excel file (.xlsx or .xls)'
                });
                return;
            }
                // Optionally, check size (e.g., < 10MB)
                resolve({ isValid: true });
            });
        },


        downloadRanking() {
            try {
                const rows = (this.partnerList || []).map((p, idx) => ({
                    '#': idx + 1,
                    'Vendor Name': p.vendor_name || p.name || '',
                    'RFPs Invited': p.rfps_invited ?? p.rfp_invited ?? 0,
                    'RFPs Won': p.rfps_won ?? p.rfp_won ?? 0,
                    'Marketing Profile': p.specialMarketing === true ? 'Yes' : p.specialMarketing === false ? 'No' : '',
                    'Accredited': p.accredited === true ? 'Yes' : p.accredited === false ? 'No' : '',
                    'Vendor Rank': p.rank ?? '',
                    'Vendor Boost': p.rank_boost ?? ''
                }));

                const worksheet = XLSX.utils.json_to_sheet(rows);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendor Ranking');

                const now = new Date();
                const pad = (n) => String(n).padStart(2, '0');
                const timestamp = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}-${pad(now.getHours())}-${pad(now.getMinutes())}`;
                const filename = `vendor-ranking-${timestamp}.xlsx`;

                XLSX.writeFile(workbook, filename);
            } catch (error) {
                console.error('Error exporting ranking:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Export Failed',
                    detail: 'Failed to generate the ranking file.',
                    life: 4000
                });
            }
        }
    }
};
</script>

<style scoped>
.card {
    padding: 1.5rem;
}

.card h5 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #495057;
}

.card p {
    margin-bottom: 1.5rem;
    color: #6c757d;
}

.uploader:hover{
    background-color: #00796b !important;
}

.upload-section {
    margin-bottom: 2rem;
    padding: 1.5rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}

.upload-header h6 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #495057;
    font-size: 1.1rem;
}

.upload-description {
    color: #6c757d;
    margin-bottom: 1rem;
    font-size: 0.9rem;
}

.instructions-title {
    font-weight: 600;
    margin-bottom: 0.25rem;
}

.instructions-list {
    margin: 0.25rem 0 0.5rem 1.25rem;
    padding: 0;
}

.instructions-list li {
    margin: 0.15rem 0;
}

.upload-controls {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.upload-actions {
    display: flex;
    /* align-items: center; */
    gap: 0.75rem;
}

.upload-hint {
    color: #6c757d;
    font-size: 0.85rem;
}

.upload-actions .p-button {
    width: 14rem !important;
    height: 2.5rem;
}

/* FileUpload (basic) lives inside a shadowed structure; use :deep to pierce scoped CSS */
:deep(.upload-actions .p-fileupload) {
    width: 14rem !important;
}

:deep(.upload-actions .p-fileupload .p-fileupload-choose) {
    width: 14rem !important;
    height: 2.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

:deep(.upload-actions .p-fileupload .p-button) {
    width: 100% !important; /* let the choose button fill 14rem container */
    height: 2.5rem;
}

</style>
